<template>
  <div v-if="!!challengeDetails" class="container-fluid">
    <ChallengeHeaderV2 :title="challengeDetails.title" :challengeId="challengeDetails.id"
      :startTimeInSeconds="challengeDuration" />
    <div class="liv-challenge-dashboard d-flex flex-wrap"
      v-if="challengeDetails.status != 'completed' && challengeDetails.status != 'stopped'">
      <div v-for="(user, index) in users" :key="user.udid">
        <UserChallengeCard :ref="`child-${index}`" :user-details="user"
          @update-user-duration="(userDuration) => user.durationInSecs = userDuration.duration"></UserChallengeCard>
      </div>
    </div>
    <ChallengeResults style="padding-left: 2rem;"
      v-if="challengeDetails.status == 'completed' || challengeDetails.status == 'stopped'" :users="users" />
  </div>
</template>

<script>
import Utils from "@/helpers/utils";
import { useRoute } from "vue-router";
import ChallengeHeaderV2 from "./ChallengeHeaderV2.vue";
import UserChallengeCard from "./UserChallengeCard.vue";
import ChallengeResults from "./ChallengeResults.vue";
import { mapMutations} from "vuex";
import Constants from "@/helpers/constants";

const CHALLENGE_API_URL = `${process.env.VUE_APP_API_BASE_URL}/api/v1/challenges`;


export default {
  name: 'LiveChallengeBoard',
  components: {
    ChallengeHeaderV2,
    ChallengeResults,
    UserChallengeCard,
  },
  data() {
    return {
      challengeDetails: null,
      users: [],
      eventSource: null,
      challengeId: null,
      challengeDuration: 0, // in seconds
      mediaRecorder: null,
      recordedChunks: [],
      videoUrl: null,
      isRecording: false,
      isInFullScreenMode: false,
    };
  },
  props: {
  },
  mounted() {
    this.challengeId = useRoute().query.challengeId;
    this.fetchChallengeDetails(this.challengeId);
    this.startRecording();
    this.setSideNavVisibility(false);
  },
  beforeUnmount() {
    console.log('LiveChallengeBoard component is being destroyed');
    if (this.eventSource) this.eventSource.close();
  },
  methods: {
    ...mapMutations(["setSideNavVisibility"]),
    listenToLiveChallenge() {
      const url = `${CHALLENGE_API_URL}/heart-rate-stream/${this.challengeId}`;
      this.eventSource = new EventSource(url,
        {
          headers: {
            'Access-Control-Allow-Origin': process.env.ORIGIN
          }
        }
      );

      this.eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        for (let i = 0; i < this.users.length; i++) {
          const user = this.users[i];
          if (data.hrData && user.udid == data.hrData.udid
            && user.status !== 'active') {
            this.$refs[`child-${i}`][0].startTimer();
          }
          if (data.fitnessData && user.udid == data.fitnessData.udid
            && user.status !== 'active') {
            this.$refs[`child-${i}`][0].startTimer();
          }
        }
        this.users.filter(user => {
          if (data.hrData && user.udid == data.hrData.udid && data.hrData.heart_rate != 255) {
            user.hr = data.hrData.heart_rate;
            user.lastSync = data.hrData.timestamp;
            user.status = 'active';
            user = this.calculateHrChallengeZoneVals(user);
            if (user.minHr == 0) {
              user.minHr = user.hr;
            }
            user.minHr = user.hr < user.minHr ? user.hr : user.minHr;
            user.maxHr = user.hr > user.maxHr ? user.hr : user.maxHr;
            user.hrList.push(user.hr);
            user.avgHr = this.calculateAverageHR(user.hrList);
          }

          if (data.fitnessData && data.fitnessData.udid === user.udid) {
            user.status = 'active';
            user.spo2 = data.fitnessData.spo2 > 0 ? data.fitnessData.spo2 : user.spo2;
            user.steps = data.fitnessData.steps;
            user.calories = data.fitnessData.calories;
            //user.maxHr = data.fitnessData.maxHr > user.maxHr ? data.fitnessData.maxHr : user.maxHr;
          }
        });
      };
    },
    calculateHrChallengeZoneVals(userDetails) {
      let hrChallengeZoneLabel = '';
      let hrChallengeZoneClass = '';
      const hrZoneRanges = Utils.getHrZoneRangeHeartRates(userDetails.age);
      if (userDetails.hr < hrZoneRanges.restZoneHr) {
        hrChallengeZoneLabel = 'RESTING';
        hrChallengeZoneClass = Constants.WORKOUT_ZONES.REST;
      } else if (userDetails.hr >= hrZoneRanges.restZoneHr &&
        userDetails.hr < hrZoneRanges.warmUpZoneHr) {
        hrChallengeZoneLabel = 'WARMUP';
        hrChallengeZoneClass = Constants.WORKOUT_ZONES.WARM_UP;
      } else if (userDetails.hr >= hrZoneRanges.warmUpZoneHr &&
        userDetails.hr < hrZoneRanges.fatBurningZoneHr) {
        hrChallengeZoneLabel = 'FAT BURNING';
        hrChallengeZoneClass = Constants.WORKOUT_ZONES.FAT_BURNING;
      } else if (userDetails.hr >= hrZoneRanges.fatBurningZoneHr &&
        userDetails.hr < hrZoneRanges.aerobicZoneHr) {
        hrChallengeZoneLabel = 'AEROBIC';
        hrChallengeZoneClass = Constants.WORKOUT_ZONES.AEROBIC;
      } else if (userDetails.hr >= hrZoneRanges.aerobicZoneHr &&
        userDetails.hr < hrZoneRanges.anAerobicZoneHr) {
        hrChallengeZoneLabel = 'ANAEROBIC';
        hrChallengeZoneClass = Constants.WORKOUT_ZONES.ANAEROBIC;
      } else if (userDetails.hr >= hrZoneRanges.anAerobicZoneHr) {
        hrChallengeZoneLabel = 'EXTREME';
        hrChallengeZoneClass = Constants.WORKOUT_ZONES.EXTREME;
      }

      userDetails.hrzChallengeLabel = hrChallengeZoneLabel;
      userDetails.hrzChallengeClass = hrChallengeZoneClass;
      return userDetails;
    },
    fetchChallengeDetails(challengeId) {
      //console.log('Fetching challenge details for:', challengeId);
      let challengeReq = {
        challenge_id: challengeId,
        action: 'start'
      };
      this.$store.dispatch('challenge/fetchChallengeDetails', challengeReq).then(response => {
        if (response && response.data) {
          this.challengeDetails = response.data.challenge;
          this.challengeDuration = this.challengeDetails.duration * 60; // Convert minutes to seconds
          this.prepareContestents(response.data.participants);
        }
      });
    },
    prepareContestents(participants) {
      this.users = participants.map(user => {
        user = this.setDefaultChallengeValues(user);
        return user;
      });
      this.listenToLiveChallenge();
    },
    setDefaultChallengeValues(user) {
      user.hr = 0;
      user.spo2 = 0;
      user.steps = 0;
      user.calories = 0;
      user.minHr = 0;
      user.avgHr = 0;
      user.maxHr = 0;
      user.durationInSecs = 0;
      user.hrZone = 'NA';
      user.age = Utils.calculateAge(user.date_of_birth);
      user.hrList = [];
      return user;
    },
    formatTime(ts) {
      const date = new Date(ts * 1000);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    stopChallenge(action) {
      console.log('Stopping challenge with ID:', this.challengeId);
      this.challengeDetails.status = action === 'HARD_STOP' ? 'stopped' : 'completed';
      for (let i = 0; i < this.users.length; i++) {
        this.$refs[`child-${i}`][0].stopTimer(); // Stop the timer for each user
      }
      this.rankByCalories(); // Rank users by calories burned
      this.$store.dispatch('challenge/stop', this.challengeId).then(response => {
        console.log('Challenge stopped successfully:', response);
        this.$snackbar.add({
          type: 'success',
          text: `Challenge ${action === 'HARD_STOP' ? 'stopped' : 'completed'} successfully.`,
        });
        this.eventSource.close(); // Close the event source connection
      }).catch(error => {
        console.error('Error stopping challenge:', error);
        this.$snackbar.add({
          type: 'error',
          text: `Failed to ${action === 'HARD_STOP' ? 'stop' : 'complete'} challenge.`,
        });
      });
    },
    saveChallengeResults() {
      let challengeResults = {
        challenge_id: this.challengeId,
        challengeUsersData: this.users.map(user => ({
          udid: user.udid,
          hr: user.hr,
          spo2: user.spo2,
          steps: user.steps,
          calories: user.calories,
          minHr: user.minHr,
          avgHr: user.avgHr,
          maxHr: user.maxHr,
          durationInSecs: user.durationInSecs,
          hrZone: user.hrzChallengeLabel,
          rank: user.rank
        }))
      };
      this.$store.dispatch('challenge/saveChallengeResults', challengeResults).then(response => {
        console.log('Challenge results saved successfully:', response);
        this.setSideNavVisibility(true);
        document.exitFullscreen();
        this.isInFullScreenMode = false; // Reset fullscreen mode
        this.$snackbar.add({
          type: 'success',
          text: 'Challenge results saved successfully.',
        });
        setTimeout(() => {
            this.stopRecording(); // Stop the recording if applicable
        }, 2000);
      }).catch(error => {
        console.error('Error saving challenge results:', error);
        this.$snackbar.add({
          type: 'error',
          text: 'Failed to save challenge results.',
        });
      });
    },
    calculateAverageHR(hrList) {
      if (hrList.length === 0) return 0;
      const total = hrList.reduce((sum, hr) => sum + hr, 0);
      return Math.round(total / hrList.length);
    },
    rankByCalories() {
      // Step 1: Clone and sort by calories descending
      const sorted = [...this.users].sort((a, b) => b.calories - a.calories);

      // Step 2: Assign ranks (handling ties if needed)
      let rank = 1;
      for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i].calories === sorted[i - 1].calories) {
          sorted[i].rank = sorted[i - 1].rank; // same rank for same calories
        } else {
          sorted[i].rank = rank;
        }
        rank++;
      }

      // Step 3: Merge ranks back into original array based on udid
      return this.users.map(item => {
        const match = sorted.find(s => s.udid === item.udid);
        return { ...item, rank: match.rank };
      });
    },
    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            displaySurface: 'browser', // optional
          },
          audio: true // captures tab/system audio if Chrome allows
        });

        this.recordedChunks = [];

        this.mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'video/webm; codecs=vp9'
        });

        this.mediaRecorder.ondataavailable = (event) => {
          if(this.isInFullScreenMode == false) {
            let fullScreenAccess = document.getElementById('app').requestFullscreen(); // to make the app fullscreen
            console.log('fullScreenAccess:', fullScreenAccess);
          }

          if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
          this.videoUrl = URL.createObjectURL(blob);

          // Auto-download
          const a = document.createElement('a');
          a.href = this.videoUrl;
          a.download = `tab-recording-${Date.now()}.webm`;
          a.click();
        };

        this.mediaRecorder.start();
        this.isRecording = true;
      } catch (error) {
        console.error('Error accessing screen:', error);
      }
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
        this.isRecording = false;
      }
    }
  }
};
</script>

<style scoped>
body {
  margin: 0;
  font-family: 'Segoe UI', sans-serif;
  background-color: #283335;
  color: #ffffff;
}

.liv-challenge-dashboard {
  /* display: grid;
  grid-template-columns: repeat(auto-fit, minmax(16.25rem, 1fr)); */
  gap: 0.75rem;
  padding-left: 1rem;
  padding-top: 1rem;
}

.card {
  background-color: #1b263b;
  border: 1px solid #2e3c4e;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 0 1rem rgba(0, 0, 0, 0.3);
  border-radius: 0.5rem;
  transition: transform 0.2s ease;
}

.card:hover {
  transform: scale(1.02);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  padding: 1rem !important;
}

.card-header .avatar {
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 0.25rem;
}

.card-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.card-header p {
  margin: 0;
  color: #ccc;
  font-size: 0.85rem;
}

.metrics {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.metric {
  display: flex;
  /* align-items: center; */
  gap: 0.5rem;
  font-size: 1rem;
  color: #e0e0e0;
}

.metric span {
  font-size: 1.2rem;
}

.metric.small {
  font-size: 0.85rem;
  color: #aaa;
}

.hr-zone {
  text-align: center;
  font-size: 13px;
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-weight: bold;
}

@keyframes pulseGlow {

  0%,
  100% {
    box-shadow: 0 0 0px rgba(255, 255, 255, 0.2);
  }

  50% {
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  }
}

.footer {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #aaa;
  margin-top: 10px;
}

/* Max Effort Special Flashing */
.flash {
  animation: flashRed 1s infinite alternate;
}

@keyframes flashRed {
  0% {
    background-color: #b71c1c;
  }

  100% {
    background-color: #d32f2f;
  }
}

/* Animations */
@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }
}

@keyframes flicker {

  0%,
  100% {
    opacity: 1;
  }

  45% {
    opacity: 0.7;
  }

  55% {
    opacity: 1;
  }

  60% {
    opacity: 0.5;
  }

  70% {
    opacity: 1;
  }
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.04);
  }
}

@keyframes floatIn {
  0% {
    transform: translateY(10px);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>